import { faExternalLink, faSpinner, faTrash } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useMutation } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { useContext, useMemo } from 'react';

import { Color } from '@/constants/color';
import AppContext from '@/contexts/appContext';
import { usePersonPersonRelationshipTypes } from '@/hooks/usePersonPersonRelationships';
import PersonContext from '@/pages/Person/PersonContext';
import PersonPersonRelationshipRepository from '@/repositories/PersonPersonRelationshipRepository';
import Avatar from '@/ui/components/Avatar';
import Button from '@/ui/components/Button';
import DataList from '@/ui/components/DataList';
import mapAxiosErrorMessages from '@/utils/mapAxiosErrorMessages';
import usFormatPhoneNumber from '@/utils/usFormatPhoneNumber';

const NOT_A_DUPLICATE_RELATIONSHIP_SLUG = 'not-the-same-as';

interface ComparisonRowProps {
  label: string;
  original: string | null | undefined;
  duplicate: string | null | undefined;
  sensitive?: boolean;
}

const ComparisonRow = ({ label, original, duplicate, sensitive = true }: ComparisonRowProps) => {
  const matches =
    !!original && !!duplicate && original.trim().toLowerCase() === duplicate.trim().toLowerCase();
  const valueClass = `${sensitive ? 'mp-sensitive ' : ''}${matches ? 'text-red-600 font-medium' : ''}`;

  return (
    <div className="py-1 sm:grid sm:grid-cols-4 sm:gap-4 sm:py-1 items-start">
      <dt className="font-semibold">{label}</dt>
      <dd className="col-span-3">
        <div className="lg:hidden space-y-1 mt-1">
          <div>
            <span className="text-xs text-gray-400">Potential Duplicate: </span>
            <span className={`text-sm ${valueClass}`}>{duplicate || '—'}</span>
          </div>
          <div>
            <span className="text-xs text-gray-400">This Profile: </span>
            <span className={`text-sm ${valueClass}`}>{original || '—'}</span>
          </div>
        </div>
        <div className="hidden lg:grid lg:grid-cols-2 gap-4">
          <span className={`text-sm ${valueClass}`}>{duplicate || '—'}</span>
          <span className={`text-sm ${valueClass}`}>{original || '—'}</span>
        </div>
      </dd>
    </div>
  );
};

const PersonDuplicatesSection = () => {
  const { addMessage } = useContext(AppContext);
  const {
    refetch: refetchPerson,
    person: {
      potential_duplicates,
      id: person_id,
      first_name,
      last_name,
      birthday,
      jurisdiction,
      default_email,
      default_phone
    }
  } = useContext(PersonContext);
  const personPersonRelationshipRepository = PersonPersonRelationshipRepository();
  const { data: relationshipTypes } = usePersonPersonRelationshipTypes(
    { slug: NOT_A_DUPLICATE_RELATIONSHIP_SLUG },
    true
  );

  const notADuplicateRelationshipId = useMemo(() => {
    if (!relationshipTypes || relationshipTypes.results.length !== 1) {
      return;
    }
    return relationshipTypes.results[0].id;
  }, [relationshipTypes]);

  const dismissMutation = useMutation({
    mutationFn: async (potentialDuplicateId: string) => {
      const data = {
        person_id: person_id,
        related_person_id: potentialDuplicateId,
        relationship_type_id: notADuplicateRelationshipId
      };
      return personPersonRelationshipRepository.createPersonPersonRelationship(data);
    },
    onSuccess: async () => {
      await refetchPerson();
      addMessage('success', 'Duplicate dismissed.');
    },
    onError: (error: AxiosError) => {
      addMessage(mapAxiosErrorMessages(error));
    }
  });

  if (potential_duplicates.length === 0) {
    return <div>No potential duplicates found</div>;
  }

  return (
    <div className="space-y-6">
      {potential_duplicates.map((duplicate) => {
        const isPending = dismissMutation.isPending && dismissMutation.variables === duplicate.id;
        const displayName =
          [duplicate.first_name, duplicate.last_name].filter(Boolean).join(' ') || 'Unknown';
        return (
          <div key={duplicate.id}>
            <div className="group/duplicate flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Avatar
                  avatarUrl={duplicate.profile_image_url ?? undefined}
                  name={displayName}
                  className="mp-sensitive"
                />
                <a
                  className="mp-sensitive text-blue-600 underline font-medium"
                  href={duplicate.external_links.homebase.url}
                  target="_blank"
                  rel="noreferrer nofollow">
                  {displayName}
                  <FontAwesomeIcon icon={faExternalLink} className="ml-1" />
                </a>
              </div>
              {notADuplicateRelationshipId && (
                <div className="invisible group-hover/duplicate:visible">
                  <Button
                    ariaLabel="Dismiss"
                    color={Color.RED}
                    disabled={dismissMutation.isPending}
                    onClick={() =>
                      confirm('Are you sure you want to dismiss this potential duplicate?')
                        ? dismissMutation.mutateAsync(duplicate.id)
                        : null
                    }
                    size="xs">
                    <FontAwesomeIcon
                      className={isPending ? 'animate-spin' : ''}
                      icon={isPending ? faSpinner : faTrash}
                    />
                  </Button>
                </div>
              )}
            </div>
            <div className="py-1 sm:grid sm:grid-cols-4 sm:gap-4 sm:py-1 items-center">
              <div />
              <div className="hidden lg:grid lg:grid-cols-2 gap-4 col-span-3">
                <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide">
                  Potential Duplicate
                </span>
                <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide">
                  This Profile
                </span>
              </div>
            </div>
            <DataList>
              <ComparisonRow
                label="First Name"
                original={first_name}
                duplicate={duplicate.first_name}
              />
              <ComparisonRow
                label="Last Name"
                original={last_name}
                duplicate={duplicate.last_name}
              />
              <ComparisonRow label="Birthday" original={birthday} duplicate={duplicate.birthday} />
              <ComparisonRow
                label="Jurisdiction"
                original={jurisdiction?.name}
                duplicate={duplicate.jurisdiction}
                sensitive={false}
              />
              <ComparisonRow
                label="Email"
                original={default_email}
                duplicate={duplicate.default_email}
              />
              <ComparisonRow
                label="Phone"
                original={usFormatPhoneNumber(default_phone)}
                duplicate={usFormatPhoneNumber(duplicate.default_phone)}
              />
            </DataList>
          </div>
        );
      })}
    </div>
  );
};

export default PersonDuplicatesSection;
